import React, { useState, useMemo } from 'react';
import { Lightbulb, AlertCircle, Sparkles, RefreshCw } from 'lucide-react';
import { getSchoolColor, getCollegeName } from './designSystem';

/**
 * AdvantagesPanel - Two-column panel showing strategic insights
 * LEFT: Your Advantages (what you bring to the school) - for essays
 * RIGHT: Aspects to Address (areas to strengthen) - for preparation
 */
const AdvantagesPanel = ({ colleges = [], advantagesData = {}, onGenerate, isGenerating = false }) => {
  const [activeSchool, setActiveSchool] = useState(0);

  console.log('🎨 [AdvantagesPanel] Render with props:', {
    collegesCount: colleges.length,
    advantagesData: advantagesData,
    advantagesDataKeys: Object.keys(advantagesData),
    isGenerating
  });

  if (colleges.length === 0) {
    console.log('⚠️ [AdvantagesPanel] No colleges - returning null');
    return null;
  }

  const activeCollege = colleges[activeSchool];

  // Safety check: if activeCollege is undefined, return null
  if (!activeCollege) {
    console.log('⚠️ [AdvantagesPanel] Active college is undefined - returning null');
    return null;
  }

  const activeColors = getSchoolColor(activeSchool);
  const collegeName = getCollegeName(activeCollege);

  console.log('📍 [AdvantagesPanel] Active college:', {
    index: activeSchool,
    name: collegeName,
    hasStudentAdvantages: !!activeCollege.student_advantages,
    hasSchoolFit: !!activeCollege.school_fit
  });

  // Extract both perspectives from the data
  const { yourAdvantages: yourAdvantagesData = {}, aspectsToAddress: aspectsToAddressData = {} } = advantagesData;

  console.log('📦 [AdvantagesPanel] Extracted from advantagesData:', {
    yourAdvantagesData: yourAdvantagesData,
    yourAdvantagesKeys: Object.keys(yourAdvantagesData),
    aspectsToAddressData: aspectsToAddressData,
    aspectsToAddressKeys: Object.keys(aspectsToAddressData)
  });

  // Get data for active college
  const yourAdvantagesList = yourAdvantagesData[collegeName] || activeCollege.student_advantages || [];
  const aspectsToAddressList = aspectsToAddressData[collegeName] || activeCollege.aspects_to_address || [];

  console.log(`🎯 [AdvantagesPanel] Data for ${collegeName}:`, {
    yourAdvantagesList: yourAdvantagesList,
    yourAdvantagesLength: yourAdvantagesList?.length,
    aspectsToAddressList: aspectsToAddressList,
    aspectsToAddressLength: aspectsToAddressList?.length
  });

  // Convert to array if needed (handle both arrays and JSON strings)
  const yourAdvantages = useMemo(() => {
    if (Array.isArray(yourAdvantagesList)) {
      console.log(`  [useMemo] yourAdvantages is array:`, yourAdvantagesList);
      return yourAdvantagesList;
    }
    if (typeof yourAdvantagesList === 'string') {
      try {
        const parsed = JSON.parse(yourAdvantagesList);
        console.log(`  [useMemo] yourAdvantages parsed from string:`, parsed);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error(`  [useMemo] Failed to parse yourAdvantages string:`, e);
        return [];
      }
    }
    console.log(`  [useMemo] yourAdvantages empty (type: ${typeof yourAdvantagesList})`);
    return [];
  }, [yourAdvantagesList]);

  const aspectsToAddress = useMemo(() => {
    if (Array.isArray(aspectsToAddressList)) {
      console.log(`  [useMemo] aspectsToAddress is array:`, aspectsToAddressList);
      return aspectsToAddressList;
    }
    if (typeof aspectsToAddressList === 'string') {
      try {
        const parsed = JSON.parse(aspectsToAddressList);
        console.log(`  [useMemo] aspectsToAddress parsed from string:`, parsed);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error(`  [useMemo] Failed to parse aspectsToAddress string:`, e);
        return [];
      }
    }
    console.log(`  [useMemo] aspectsToAddress empty (type: ${typeof aspectsToAddressList})`);
    return [];
  }, [aspectsToAddressList]);

  // Check if there's any actual data
  const hasYourAdvantages = yourAdvantages.length > 0;
  const hasAspectsToAddress = aspectsToAddress.length > 0;
  const hasAnyData = hasYourAdvantages || hasAspectsToAddress;

  console.log('✨ [AdvantagesPanel] Final render decision:', {
    hasYourAdvantages,
    hasAspectsToAddress,
    hasAnyData,
    willShowEmptyState: !hasAnyData && onGenerate
  });

  // If no data at all, show empty state
  if (!hasAnyData && onGenerate) {
    return (
      <div className="bg-white rounded-lg border-2 border-slate-300">
        {/* Section Header */}
        <div className="p-6 border-b-2 border-slate-200">
          <h3 className="text-xl font-bold text-slate-900">Strategic Application Insights</h3>
          <p className="text-sm text-slate-600 mt-1">Dual-perspective analysis for essays and selection</p>
        </div>

        {/* College Tabs */}
        <div className="flex gap-2 p-4 border-b border-slate-200 overflow-x-auto">
          {colleges.map((college, idx) => {
            const colors = getSchoolColor(idx);
            const name = getCollegeName(college);
            return (
              <button
                key={college.id || idx}
                disabled
                className={`px-4 py-2 rounded-lg font-medium border-2 whitespace-nowrap ${colors.bg} ${colors.text} border-${colors.borderLight} opacity-60`}
              >
                {name}
              </button>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="p-8 text-center">
          <div className="max-w-2xl mx-auto mb-6">
            <p className="text-sm text-slate-700 leading-relaxed mb-4">
              Generate strategic insights with <strong>two perspectives</strong>:
            </p>
            <div className="grid grid-cols-2 gap-4 text-left">
              <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-4">
                <h4 className="font-bold text-teal-900 text-sm mb-2">Your Advantages</h4>
                <p className="text-xs text-teal-800">
                  What you bring to the school - use for "Why Us?" essays
                </p>
              </div>
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                <h4 className="font-bold text-amber-900 text-sm mb-2">Aspects to Address</h4>
                <p className="text-xs text-amber-800">
                  Areas to strengthen - for application preparation
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onGenerate}
            disabled={isGenerating}
            className="px-6 py-3 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto border-2 border-slate-900 transition-all active:scale-95"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Strategic Insights
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-slide-in">
      <h2 className="text-xl font-bold text-slate-900 mb-6">Strategic Application Insights</h2>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {colleges.map((college, idx) => {
          const colors = getSchoolColor(idx);
          const isActive = activeSchool === idx;
          const name = getCollegeName(college);

          return (
            <button
              key={college.id || idx}
              onClick={() => setActiveSchool(idx)}
              className={`
                px-6 py-3 rounded-lg font-medium border-2 whitespace-nowrap transition-all active:scale-95
                ${isActive
                  ? `${colors.accent} text-white border-slate-900`
                  : `${colors.bg} ${colors.text} border-${colors.borderLight} hover:opacity-80`
                }
              `}
            >
              {name}
            </button>
          );
        })}
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN: Your Advantages */}
        <div className="bg-white border-2 border-teal-500 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-teal-500 text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lightbulb className="w-6 h-6 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg">Your Advantages</h3>
                <p className="text-xs text-teal-100">What you bring to {collegeName}</p>
              </div>
            </div>
            {onGenerate && (
              <button
                onClick={onGenerate}
                disabled={isGenerating}
                className="p-2 hover:bg-teal-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Regenerate advantages"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`}
                />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {hasYourAdvantages ? (
              <div className="space-y-3">
                {yourAdvantages.map((advantage, idx) => {
                  // Handle both string and object advantages defensively
                  if (typeof advantage === 'string') {
                    return (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-teal-500 mt-1 flex-shrink-0 font-bold">•</span>
                        <span className="text-sm text-slate-700 leading-relaxed">{advantage}</span>
                      </div>
                    );
                  }

                  // Handle object advantage with proper fallbacks
                  const text = advantage?.title || advantage?.advantage || advantage?.description || advantage?.narrative || 'No description available';

                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="text-teal-500 mt-1 flex-shrink-0 font-bold">•</span>
                      <span className="text-sm text-slate-700 leading-relaxed">{text}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500 italic text-sm">
                  No advantages generated yet
                </p>
              </div>
            )}

            {/* Essay Tip */}
            {hasYourAdvantages && (
              <div className="mt-6 bg-teal-50 border-l-4 border-teal-500 p-4 rounded">
                <p className="text-xs font-medium text-teal-900">
                  <strong>💡 Essay Strategy:</strong> Use these points in your "Why Us?" supplemental essay to show how you'll contribute to campus.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Aspects to Address */}
        <div className="bg-white border-2 border-amber-500 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-amber-500 text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg">Aspects to Address</h3>
                <p className="text-xs text-amber-100">Areas to strengthen for {collegeName}</p>
              </div>
            </div>
            {onGenerate && (
              <button
                onClick={onGenerate}
                disabled={isGenerating}
                className="p-2 hover:bg-amber-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Regenerate aspects to address"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`}
                />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {hasAspectsToAddress ? (
              <div className="space-y-3">
                {aspectsToAddress.map((aspect, idx) => {
                  // Handle both string and object aspects defensively
                  if (typeof aspect === 'string') {
                    return (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-1 flex-shrink-0 font-bold">•</span>
                        <span className="text-sm text-slate-700 leading-relaxed">{aspect}</span>
                      </div>
                    );
                  }

                  // Handle object aspect with proper fallbacks
                  const text = aspect?.title || aspect?.aspect || aspect?.description || aspect?.narrative || 'No description available';

                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="text-amber-500 mt-1 flex-shrink-0 font-bold">•</span>
                      <span className="text-sm text-slate-700 leading-relaxed">{text}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500 italic text-sm">
                  No aspects generated yet
                </p>
              </div>
            )}

            {/* Application Tip */}
            {hasAspectsToAddress && (
              <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                <p className="text-xs font-medium text-amber-900">
                  <strong>📋 Application Strategy:</strong> Use these insights to strengthen your profile and address potential gaps before applying.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Regenerate Button */}
      {hasAnyData && onGenerate && (
        <div className="mt-6 text-center">
          <button
            onClick={onGenerate}
            disabled={isGenerating}
            className="px-6 py-3 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto transition-all active:scale-95"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Regenerate Insights
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdvantagesPanel;
